import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import BusinessLayout from './components/layout/BusinessLayout';
import EmployeeLayout from './components/layout/EmployeeLayout';

// ErrorBoundary — catches render errors to prevent full SPA crash
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <h2>Something went wrong</h2>
          <p style={{ color: '#666', marginTop: '8px' }}>Please refresh the page to continue.</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: '16px', padding: '8px 24px', background: '#A855DC',
              color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer'
            }}
          >
            Refresh Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Adaptive Layout: Uses BusinessLayout for owners/managers, EmployeeLayout for employees
const AdaptiveLayout = ({ children }) => {
  const { user } = useAuth();
  const isOwnerOrManager = user?.role === 1 || user?.role === 2;

  return isOwnerOrManager ? (
    <BusinessLayout>{children}</BusinessLayout>
  ) : (
    <EmployeeLayout>{children}</EmployeeLayout>
  );
};

// Import global styles
import './styles/globals.css';
import './App.css';

const { lazy, Suspense } = React;

// Lazy-loaded page components — split into separate chunks for faster initial load
const LandingPage = lazy(() => import('./pages/public/LandingPage/LandingPage'));
const LoginPage = lazy(() => import('./pages/public/auth/LoginPage/LoginPage'));
const RegisterPage = lazy(() => import('./pages/public/auth/RegisterPage/RegisterPage'));
const ResetPasswordPage = lazy(() => import('./pages/public/auth/ResetPasswordPage/ResetPasswordPage'));
const EducationHomepage = lazy(() => import('./pages/public/education/EducationHomepage'));
const CourseDetailsPage = lazy(() => import('./pages/public/education/CourseDetailsPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/public/PrivacyPolicy/PrivacyPolicyPage'));

const BusinessDashboard = lazy(() => import('./pages/business/Dashboard/BusinessDashboard'));
const BusinessProfile = lazy(() => import('./pages/business/Profile/BusinessPage'));
const BusinessSettingsPage = lazy(() => import('./pages/business/Settings/BusinessSettingsPage'));
const EmployeeManagement = lazy(() => import('./pages/business/Employees/EmployeePage'));
const InvitesPage = lazy(() => import('./pages/business/Invites/InvitesPage'));
// [DISABLED] Checklists pages — hidden per business decision, re-enable when ready to launch as new features
// const ChecklistManagement = lazy(() => import('./pages/business/Checklists/ChecklistPage'));
// [DISABLED] HR Policies & MVV pages — hidden per business decision, re-enable when ready to launch as new features
// const PolicyManagement = lazy(() => import('./pages/business/Policies/HRPoliciesPage'));
// const CompanyValues = lazy(() => import('./pages/business/CompanyValues/MVVPage'));
const BusinessBookingPage = lazy(() => import('./pages/business/Bookings/BusinessBookingPage'));
const GoalManagementPage_Business = lazy(() => import('./pages/business/GoalSetting/GoalManagementPage_Business'));
const StockManagementPage = lazy(() => import('./pages/business/Stock/StockManagementPage'));
const POSPage = lazy(() => import('./pages/business/POS/POSPage'));
const VoucherManagementPage = lazy(() => import('./pages/business/Vouchers/VoucherManagementPage'));
const TimeTrackingDashboard = lazy(() => import('./pages/business/TimeTracking/TimeTrackingDashboard'));
const ClientsPage = lazy(() => import('./pages/business/Clients/ClientsPage'));
const ManageServicesPage = lazy(() => import('./pages/business/Services/ManageServicesPage'));
const WorkstationsPage = lazy(() => import('./pages/business/Workstations/WorkstationsPage'));
// [DISABLED] Leave pages — hidden per business decision, re-enable when ready to launch as new features
// const LeaveManagementPage = lazy(() => import('./pages/business/Leave/LeaveManagementPage'));
const BillingPage = lazy(() => import('./pages/business/Billing/BillingPage'));

const EmployeeBookingsPage = lazy(() => import('./pages/employee/Bookings/EmployeeBookingsPage'));
const NewClientPage = lazy(() => import('./pages/employee/Clients/NewClientPage'));
const EmployeeDashboard = lazy(() => import('./pages/employee/Dashboard/EmployeeDashboard'));
// [DISABLED] My Targets (read-only performance viewer) — replaced by EmployeeGoalConfigurationPage on 2026-04-24. Old page preserved on disk at ./pages/employee/GoalTracking/GoalTrackingPage.jsx for future re-use if we want the rich performance dashboard back. Re-enable by uncommenting the import + swapping the route below.
// const GoalTrackingPage = lazy(() => import('./pages/employee/GoalTracking/GoalTrackingPage'));
const EmployeeGoalConfigurationPage = lazy(() => import('./pages/employee/GoalTracking/EmployeeGoalConfigurationPage'));
const SchedulePage = lazy(() => import('./pages/employee/Schedule/SchedulePage'));
// [DISABLED] Checklists pages — hidden per business decision, re-enable when ready to launch as new features
// const EmployeeChecklistPage = lazy(() => import('./pages/employee/Checklist/EmployeeChecklistPage'));
// [DISABLED] HR Policies & MVV pages — hidden per business decision, re-enable when ready to launch as new features
// const EmployeeHRPoliciesPage = lazy(() => import('./pages/employee/Policies/EmployeeHRPoliciesPage'));
// const EmployeeMVVPage = lazy(() => import('./pages/employee/CompanyValues/EmployeeMVVPage'));
const EmployeeProfilePage = lazy(() => import('./pages/employee/Profile/EmployeeProfilePage'));
// [DISABLED] Leave pages — hidden per business decision, re-enable when ready to launch as new features
// const EmployeeLeavePage = lazy(() => import('./pages/employee/Leave/EmployeeLeavePage'));

const ClockInPage = lazy(() => import('./pages/employee/ClockIn/ClockInPage'));
const ClockOutPage = lazy(() => import('./pages/employee/ClockOut/ClockOutPage'));
const SupportPage = lazy(() => import('./pages/common/Support/SupportPage'));
const BusinessDashboardDark = lazy(() => import('./pages/business/Dashboard/BusinessDashboardDark'));

// Loading fallback for lazy-loaded routes
const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <div style={{ width: '40px', height: '40px', border: '4px solid #e0e0e0', borderTop: '4px solid #A855DC', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
  </div>
);

// Component to handle root page - show landing or redirect if authenticated
const RootPage = () => {
  const { isAuthenticated, loading } = useAuth();

  // SECURITY: Block rendering until auth state is resolved
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #e0e0e0',
          borderTop: '4px solid #A855DC',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '16px'
        }}></div>
        <div>Loading...</div>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // Redirect to unified dashboard when authenticated
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  // Show landing page at root when not authenticated
  return <LandingPage />;
};

function App() {
  return (
    <AuthProvider>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
        <div className="App">
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<RootPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/education" element={<EducationHomepage />} />
            <Route path="/course/:courseId" element={<CourseDetailsPage />} />

            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            
            {/* ===== DASHBOARD ROUTE - Shows correct dashboard based on user type ===== */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <SubscriptionGuard>
                  <DashboardRouter />
                </SubscriptionGuard>
              </ProtectedRoute>
            } />

            {/* ===== BUSINESS ROUTES - Persistent BusinessLayout (no remount between pages) ===== */}
            <Route element={
              <ProtectedRoute allowedRoles={['business']}>
                <BusinessLayout />
              </ProtectedRoute>
            }>
              <Route path="/business-profile" element={<BusinessProfile />} />
              <Route path="/business-settings" element={<BusinessSettingsPage />} />
              <Route path="/business/goal-setting" element={<GoalManagementPage_Business />} />
              <Route path="/employees" element={<EmployeeManagement />} />
              <Route path="/invites" element={<InvitesPage />} />
              <Route path="/business/bookings" element={<BusinessBookingPage />} />
              <Route path="/business/stock" element={<StockManagementPage />} />
              <Route path="/business/pos" element={<POSPage />} />
              <Route path="/business/vouchers" element={<VoucherManagementPage />} />
              <Route path="/business/time-tracking" element={<TimeTrackingDashboard />} />
              <Route path="/business/clients" element={<ClientsPage />} />
              <Route path="/business/services" element={<ManageServicesPage />} />
              <Route path="/business/workstations" element={<WorkstationsPage />} />
              {/* [DISABLED] Leave routes — hidden per business decision, re-enable when ready to launch as new features */}
              {/* <Route path="/business/leave" element={<LeaveManagementPage />} /> */}
              {/* <Route path="/business/my-leave" element={<EmployeeLeavePage />} /> */}
              <Route path="/business/billing" element={<BillingPage />} />
              {/* [DISABLED] Checklists — hidden per business decision, re-enable when ready to launch as new features */}
              {/* <Route path="/checklists" element={<ChecklistManagement />} /> */}
              <Route path="/business/dashboard-dark" element={<BusinessDashboardDark />} />
            </Route>

            {/* Backward compatibility - redirect old path to new */}
            <Route path="/send-invites" element={<Navigate to="/invites" replace />} />

            {/* ===== EMPLOYEE ROUTES - Persistent EmployeeLayout (no remount between pages) ===== */}
            {/* AdaptiveLayout routes stay standalone — owners/managers access these with BusinessLayout */}
            <Route path="/employee/profile" element={
              <ProtectedRoute allowedRoles={['employee']}>
                <AdaptiveLayout>
                  <EmployeeProfilePage />
                </AdaptiveLayout>
              </ProtectedRoute>
            } />

            {/* [DISABLED] Leave routes — hidden per business decision, re-enable when ready to launch as new features */}
            {/* <Route path="/employee/leave" element={
              <ProtectedRoute allowedRoles={['employee']}>
                <AdaptiveLayout>
                  <EmployeeLeavePage />
                </AdaptiveLayout>
              </ProtectedRoute>
            } /> */}

            <Route element={
              <ProtectedRoute allowedRoles={['employee']}>
                <EmployeeLayout />
              </ProtectedRoute>
            }>
              <Route path="/employee/goal-tracking" element={<EmployeeGoalConfigurationPage />} />
              <Route path="/employee/schedule" element={<SchedulePage />} />
              {/* [DISABLED] Checklists — hidden per business decision, re-enable when ready to launch as new features */}
              {/* <Route path="/employee/checklists" element={<EmployeeChecklistPage />} /> */}
              <Route path="/employee/clock-in" element={<ClockInPage />} />
              <Route path="/employee/clock-out" element={<ClockOutPage />} />
            </Route>

            {/* Redirect legacy unified clock route to clock-in (will auto-redirect to clock-out if already clocked in) */}
            <Route path="/employee/clock" element={<Navigate to="/employee/clock-in" replace />} />

            {/* [DISABLED] HR Policies & MVV routes — hidden per business decision, re-enable when ready to launch as new features
            <Route path="/employee/policies" element={<EmployeeHRPoliciesPage />} />
            <Route path="/employee/company-values" element={<EmployeeMVVPage />} />
            <Route path="/policies" element={<PolicyManagement />} />
            <Route path="/company-values" element={<CompanyValues />} />
            */}

            <Route path="/employee/pos" element={
              <ProtectedRoute allowedRoles={['employee']}>
                <AdaptiveLayout>
                  <POSPage />
                </AdaptiveLayout>
              </ProtectedRoute>
            } />

            <Route path="/employee/bookings" element={
              <ProtectedRoute allowedRoles={['employee']}>
                <AdaptiveLayout>
                  <EmployeeBookingsPage />
                </AdaptiveLayout>
              </ProtectedRoute>
            } />

            <Route path="/employee/clients/new" element={
              <ProtectedRoute allowedRoles={['employee']}>
                <AdaptiveLayout>
                  <NewClientPage />
                </AdaptiveLayout>
              </ProtectedRoute>
            } />

            {/* ===== SUPPORT - Accessible to all authenticated users ===== */}
            <Route path="/support" element={
              <ProtectedRoute>
                <ConditionalLayout>
                  <SupportPage />
                </ConditionalLayout>
              </ProtectedRoute>
            } />

            {/* Catch all - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
        </Suspense>
        </ErrorBoundary>
      </Router>
    </AuthProvider>
  );
}

// Redirect business users to billing if subscription not active
const SubscriptionGuard = ({ children }) => {
  const { hasFullAccess, userType } = useAuth();
  if (userType === 'business' && !hasFullAccess) {
    return <Navigate to="/business/billing" replace />;
  }
  return children;
};

// Component that routes to correct dashboard based on user type
const DashboardRouter = () => {
  const { userType } = useAuth();

  if (userType === 'business') {
    return (
      <BusinessLayout>
        <BusinessDashboard />
      </BusinessLayout>
    );
  } else {
    return (
      <EmployeeLayout>
        <EmployeeDashboard />
      </EmployeeLayout>
    );
  }
};

// Component that chooses layout based on user type for shared routes
const ConditionalLayout = ({ children }) => {
  const { userType } = useAuth();

  if (userType === 'business') {
    return <BusinessLayout>{children}</BusinessLayout>;
  } else {
    return <EmployeeLayout>{children}</EmployeeLayout>;
  }
};

export default App;